import { Fragment, memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { ElasticModal } from 'basic-components';
import { SymbolSearchQuery } from 'dashboard-services/api/dataStorage';
import Query from 'dashboard-services/api/Query';
import { Fetching } from 'primary-components';

import getApiConfig from 'api/ApiConfig';
import { dataStorageQueries } from 'queries/datastorage';
import { CurvesUtils } from 'utils';

import Row from './Row';

import { When } from 'react-if';

import './CurveModal.scss';

const getSearchQuery = ({ groupName, metadatasFilter = {} }) => 
    new SymbolSearchQuery.Builder()
      .withMetadatas(Object.entries(metadatasFilter).map(([key, value]) => ({ key, value })))
      .withGroupName(`*${String(groupName).toLowerCase()}*`, false)
      .build()
      .getQuery()

const useSearchSymbols = ({ selectedCurve }) => {
  const dispatch = useDispatch(),
        getQueryBuilder = () => new Query.Builder(dispatch(getApiConfig()))
          .withFetching(false)
          .withHeader("Target", selectedCurve.source, true),
        buildedQuery = getSearchQuery({ groupName: selectedCurve.groupName, metadatasFilter: { [CurvesUtils.ROOT]: [selectedCurve.root], [CurvesUtils.DATA_TYPE]: [CurvesUtils.CC_ROOT] } }),
        { data: curveRaw, isPending, isFetchingNextPage } = dataStorageQueries.useSearchSymbols({
          query: buildedQuery,
          getQueryBuilder,
          size: 1,
          enabled: !!(selectedCurve.groupName && selectedCurve.root),
          shouldRefetch: false,
        }),
        curve = useMemo(() => curveRaw?.pages?.flatMap(page => page?.items)?.[0], [curveRaw])

  return { curve, isPending, isFetchingNextPage };
}

const EMPTY_OBJ = {};
const CurveModal = ({ index, onClose }) => {
  const selectedCurve = useSelector(state => state.curvesState.creatorState.selectedCurves[index]) || EMPTY_OBJ,
        optionalTextLocal = useMemo(() => `In group: ${selectedCurve.groupName}`, [selectedCurve]),
        { curve, isPending, isFetchingNextPage } = useSearchSymbols({ selectedCurve })

  return (
    <ElasticModal
        className="curve-modal"
        onClose={onClose}
        optionalText={optionalTextLocal}
        title={"Curve information"}
    >
      <Fetching isFetching={isPending || isFetchingNextPage}/>
      <When condition={Object.keys(curve?.symbols || {}).length > 0}>
        {() => (
          <Fragment>
            <div className="curve-modal__header">
              Keys:
            </div>
            {Object.entries(curve?.symbols || {}).map(([key, value]) => (
              <Row
                  key={`curve-modal_symbol_row_${key}_${value}`}
                  label={key}
                  value={value}
              />
            ))}
          </Fragment>
        )}
      </When>
      <When condition={Object.keys(curve?.metadata || {}).length > 0}>
        {() => (
          <Fragment>
            <div className="curve-modal__header">
              Metadata:
            </div>
            {Object.entries(curve?.metadata || {}).map(([key, value]) => (
              <Row
                  key={`curve-modal_md_row_${key}_${value}`}
                  label={key}
                  value={value}
              />
            ))}
          </Fragment>
        )}
      </When>
      <When condition={!!selectedCurve?.source && !(isPending || isFetchingNextPage)}>
        {() => (
          <Fragment>
            <div className="curve-modal__header">
              Details:
            </div>
            <Row
                key={`curve-modal_details_row_source_${selectedCurve.source}`}
                label="Source"
                value={selectedCurve.source}
            />
          </Fragment>
        )}
      </When>
    </ElasticModal>
  )
}

CurveModal.defaultProps = {
  index: undefined,
}

CurveModal.propTypes = {
  index: PropTypes.number,
  onClose: PropTypes.func.isRequired,
}

export default memo(CurveModal);